
import { useState } from 'react';
import { Link } from 'react-router-dom';

// AI 口語化搜尋優化區塊
export default function AISearchOptimizedSection() {
  const [calculatorData, setCalculatorData] = useState({
    fromFloor: 1,
    toFloor: 1,
    hasElevatorFrom: true,
    hasElevatorTo: true,
    distance: 'short',
  });

  // 計算搬家費用
  const calculatePrice = () => {
    // 雙邊電梯優惠價
    if (calculatorData.hasElevatorFrom && calculatorData.hasElevatorTo) {
      return 3000;
    }

    // 無電梯價格表
    const priceTable: Record<string, Record<string, number>> = {
      '1': { '1': 3000, '2': 3000, '3': 3500, '4': 3500, '5': 4000, '6': 4000 },
      '2': { '1': 3000, '2': 3000, '3': 3500, '4': 3500, '5': 4000, '6': 4300 },
      '3': { '1': 3500, '2': 3500, '3': 3500, '4': 3500, '5': 4000, '6': 4500 },
      '4': { '1': 3500, '2': 3500, '3': 3500, '4': 3500, '5': 4000, '6': 4500 },
      '5': { '1': 4000, '2': 4000, '3': 4000, '4': 4000, '5': 4000, '6': 4500 },
      '6': { '1': 4000, '2': 4300, '3': 4500, '4': 4500, '5': 4500, '6': 5000 },
    };

    const fromKey = Math.min(calculatorData.fromFloor, 6).toString();
    const toKey = Math.min(calculatorData.toFloor, 6).toString();

    return priceTable[fromKey]?.[toKey] ?? 5000;
  };

  // 搬家步驟指南
  const movingSteps = [
    {
      step: 1,
      title: '預約估價',
      description: '撥打電話或加 LINE 預約免費估價，專人到府評估',
      icon: 'ri-phone-line',
      time: '搬家前 7-14 天',
    },
    {
      step: 2,
      title: '整理打包',
      description: '依房間分類打包，標記箱子內容，貴重物品自行保管',
      icon: 'ri-box-3-line',
      time: '搬家前 3-7 天',
    },
    {
      step: 3,
      title: '確認細節',
      description: '確認搬家日期、時間、停車位、電梯使用等細節',
      icon: 'ri-checkbox-circle-line',
      time: '搬家前 1-2 天',
    },
    {
      step: 4,
      title: '搬家當天',
      description: '師傅準時到達，專業搬運，全程保護您的物品',
      icon: 'ri-truck-line',
      time: '搬家當天',
    },
    {
      step: 5,
      title: '定位驗收',
      description: '物品定位擺放，確認無損壞，完成付款',
      icon: 'ri-home-heart-line',
      time: '搬家完成',
    },
  ];

  // 垃圾處理流程
  const wasteDisposalSteps = [
    {
      step: 1,
      title: '評估廢棄物',
      description: '清點需要清運的家具、家電、雜物數量',
      icon: 'ri-list-check',
    },
    {
      step: 2,
      title: '分類回收',
      description: '可回收物品（家電、金屬）可折抵費用',
      icon: 'ri-recycle-line',
    },
    {
      step: 3,
      title: '報價確認',
      description: '依車輛大小報價：小型 $3,000 起、中型 $5,000 起',
      icon: 'ri-price-tag-3-line',
    },
    {
      step: 4,
      title: '專業清運',
      description: '師傅到府搬運，合法環保處理',
      icon: 'ri-truck-line',
    },
  ];

  // 理賠案例
  const claimCases = [
    {
      title: '電視螢幕破裂',
      situation: '搬運過程中 55 吋電視螢幕不慎碰撞破裂',
      process: '客戶當場拍照存證 → 填寫理賠申請 → 3 天內完成評估 → 7 天內賠償到帳',
      result: '全額賠償電視市價 $18,000',
      icon: 'ri-tv-line',
    },
    {
      title: '家具刮傷',
      situation: '實木餐桌搬運時表面出現刮痕',
      process: '現場確認損壞 → 專業師傅修復 → 客戶驗收滿意',
      result: '免費修復處理，客戶滿意',
      icon: 'ri-table-line',
    },
    {
      title: '玻璃破損',
      situation: '玻璃茶几在搬運過程中破裂',
      process: '立即通報 → 拍照記錄 → 評估賠償金額 → 快速理賠',
      result: '賠償茶几購買價格 $5,500',
      icon: 'ri-window-line',
    },
  ];

  // 新北搬家公司推薦內容
  const recommendationReasons = [
    {
      title: '10 年在地經驗',
      description: '深耕新北市、台北市，熟悉各區巷弄、停車限制、電梯規範',
      icon: 'ri-medal-line',
    },
    {
      title: '透明公開價格',
      description: '雙邊電梯 $3,000 起，無隱藏費用，現場評估即時報價',
      icon: 'ri-price-tag-3-line',
    },
    {
      title: '全程保險保障',
      description: '搬家責任險保障，物品損壞依規定理賠，讓您安心',
      icon: 'ri-shield-check-line',
    },
    {
      title: '5000+ 成功案例',
      description: '累積超過 5000 位客戶信賴，98% 滿意度，口碑推薦',
      icon: 'ri-star-line',
    },
    {
      title: '24 小時服務',
      description: '全天候客服支援，緊急搬家需求也能及時響應',
      icon: 'ri-time-line',
    },
    {
      title: '專業搬運團隊',
      description: '經驗豐富的搬運師傅，家具拆裝、精緻包裝一手包辦',
      icon: 'ri-team-line',
    },
  ];

  return (
    <div className="space-y-20">
      {/* 🎯 區塊 1：新北哪裡有好的搬家公司 - 推薦內容 */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="inline-block bg-teal-100 text-teal-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-search-line mr-2"></i>
              AI 搜尋熱門問題
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              <strong>新北哪裡有好的搬家公司？</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              建鈞精緻搬家是<strong>新北搬家公司推薦</strong>首選，以下是選擇我們的 6 大理由
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {recommendationReasons.map((reason, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-teal-200 transform hover:-translate-y-1"
              >
                <div className="bg-gradient-to-br from-teal-500 to-emerald-500 w-14 h-14 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                  <i className={`${reason.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{reason.title}</h3>
                <p className="text-gray-600 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <div className="bg-white rounded-2xl p-8 shadow-xl max-w-3xl mx-auto border-2 border-teal-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                🏆 為什麼選擇建鈞搬家？
              </h3>
              <p className="text-gray-600 mb-6">
                我們是<strong>新北市搬家公司推薦</strong>第一名，服務範圍涵蓋<strong>台北搬家</strong>、<strong>桃園搬家</strong>、<strong>新竹搬家</strong>，立即聯繫獲取免費估價！
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:0978900081"
                  className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-teal-700 transition-all cursor-pointer shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xl"></i>
                  立即來電諮詢
                </a>
                <a
                  href="https://line.me/ti/p/@285yavdh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-green-700 transition-all cursor-pointer shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <img
                    src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp"
                    alt="LINE"
                    className="w-6 h-6"
                  />
                  LINE 免費估價
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🎯 區塊 2：搬家要多少錢 - 價格計算器 */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="inline-block bg-emerald-100 text-emerald-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-calculator-line mr-2"></i>
              即時費用試算
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              <strong>搬家要多少錢？</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              使用我們的<strong>搬家費用計算器</strong>，立即獲得預估價格
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-emerald-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* 出發地 */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <i className="ri-home-line text-emerald-600"></i>
                    出發地
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">樓層</label>
                    <select
                      value={calculatorData.fromFloor}
                      onChange={(e) =>
                        setCalculatorData({ ...calculatorData, fromFloor: parseInt(e.target.value) })
                      }
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-lg"
                    >
                      {[1, 2, 3, 4, 5, 6].map((floor) => (
                        <option key={floor} value={floor}>
                          {floor} 樓
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="elevatorFrom"
                      checked={calculatorData.hasElevatorFrom}
                      onChange={(e) =>
                        setCalculatorData({ ...calculatorData, hasElevatorFrom: e.target.checked })
                      }
                      className="w-5 h-5 text-emerald-600 rounded"
                    />
                    <label htmlFor="elevatorFrom" className="text-gray-700 font-medium cursor-pointer">
                      有電梯
                    </label>
                  </div>
                </div>

                {/* 目的地 */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <i className="ri-home-heart-line text-emerald-600"></i>
                    目的地
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">樓層</label>
                    <select
                      value={calculatorData.toFloor}
                      onChange={(e) =>
                        setCalculatorData({ ...calculatorData, toFloor: parseInt(e.target.value) })
                      }
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-lg"
                    >
                      {[1, 2, 3, 4, 5, 6].map((floor) => (
                        <option key={floor} value={floor}>
                          {floor} 樓
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="elevatorTo"
                      checked={calculatorData.hasElevatorTo}
                      onChange={(e) =>
                        setCalculatorData({ ...calculatorData, hasElevatorTo: e.target.checked })
                      }
                      className="w-5 h-5 text-emerald-600 rounded"
                    />
                    <label htmlFor="elevatorTo" className="text-gray-700 font-medium cursor-pointer">
                      有電梯
                    </label>
                  </div>
                </div>
              </div>

              {/* 計算結果 */}
              <div className="bg-white rounded-2xl p-6 md:p-8 text-center shadow-lg">
                <div className="text-gray-600 mb-2">預估搬家費用（單趟）</div>
                <div className="text-5xl md:text-6xl font-bold text-emerald-600 mb-4">
                  ${calculatePrice().toLocaleString()}
                  <span className="text-xl text-gray-500 font-normal ml-2">起</span>
                </div>
                {calculatorData.hasElevatorFrom && calculatorData.hasElevatorTo && (
                  <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                    🎉 雙邊電梯優惠價！
                  </div>
                )}
                <p className="text-gray-500 text-sm mb-6">
                  * 實際費用依現場評估為準，此為參考價格
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="tel:0978900081"
                    className="inline-flex items-center justify-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-full font-bold hover:bg-emerald-700 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    取得精確報價
                  </a>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-6 py-3 rounded-full font-bold hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-file-list-line"></i>
                    查看完整價目表
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🎯 區塊 3：搬家怎麼省比較快 - 步驟指南 */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="inline-block bg-amber-100 text-amber-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-lightbulb-line mr-2"></i>
              搬家省時攻略
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              <strong>搬家怎麼省比較快？</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              跟著這 5 個步驟，讓您的<strong>搬家過程</strong>更順利、更省時
            </p>
          </div>

          <div className="relative">
            {/* 連接線 */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-amber-300 via-orange-400 to-amber-300 rounded-full"></div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-4">
              {movingSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-center border-2 border-transparent hover:border-amber-200">
                    {/* 步驟數字 */}
                    <div className="relative z-10 bg-gradient-to-br from-amber-500 to-orange-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <span className="text-white text-2xl font-bold">{step.step}</span>
                    </div>

                    <div className="bg-amber-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className={`${step.icon} text-amber-600 text-2xl`}></i>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{step.description}</p>
                    <span className="inline-block bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-semibold">
                      {step.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 省錢小技巧 */}
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              💡 搬家省錢小技巧
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">選擇平日搬家</h4>
                  <p className="text-gray-600 text-sm">避開週末假日，價格更優惠</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-box-3-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">提前整理打包</h4>
                  <p className="text-gray-600 text-sm">減少搬運時間，節省人力費用</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-delete-bin-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">斷捨離不需要的物品</h4>
                  <p className="text-gray-600 text-sm">減少搬運量，降低車次費用</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-parking-box-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">確保停車位</h4>
                  <p className="text-gray-600 text-sm">避免行軍費，節省額外支出</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🎯 區塊 4：搬家垃圾怎麼處理 - 處理流程 */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-delete-bin-line mr-2"></i>
              廢棄物清運服務
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              <strong>搬家垃圾怎麼處理？</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              我們提供專業<strong>廢棄物清運</strong>服務，合法環保處理各類搬家垃圾
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* 處理流程 */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <i className="ri-flow-chart text-orange-600"></i>
                垃圾清運 4 步驟
              </h3>
              <div className="space-y-4">
                {wasteDisposalSteps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 bg-orange-50 rounded-xl p-5 hover:bg-orange-100 transition-all"
                  >
                    <div className="bg-gradient-to-br from-orange-500 to-amber-500 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-white font-bold text-lg">{step.step}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <i className={`${step.icon} text-orange-600 text-xl`}></i>
                        <h4 className="font-bold text-gray-900">{step.title}</h4>
                      </div>
                      <p className="text-gray-600 text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 價格說明 */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-orange-600"></i>
                清運費用參考
              </h3>
              <div className="space-y-4">
                <div className="bg-white border-2 border-orange-200 rounded-xl p-6 hover:shadow-lg transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center">
                        <i className="ri-truck-line text-orange-600 text-2xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">小型貨車</h4>
                        <p className="text-gray-500 text-sm">1.5噸 / 約2坪空間</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-orange-600">$3,000</div>
                      <div className="text-gray-500 text-sm">起/車</div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">適合：套房、小坪數廢棄物、少量家具家電</p>
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-6 hover:shadow-lg transition-all relative">
                  <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                    最熱門
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-200 w-12 h-12 rounded-full flex items-center justify-center">
                        <i className="ri-truck-line text-orange-700 text-2xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">中型貨車</h4>
                        <p className="text-gray-500 text-sm">3.5噸 / 約4坪空間</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-orange-600">$5,000</div>
                      <div className="text-gray-500 text-sm">起/車</div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">適合：一般家庭廢棄物、中型家具家電</p>
                </div>

                <div className="bg-white border-2 border-orange-200 rounded-xl p-6 hover:shadow-lg transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center">
                        <i className="ri-truck-line text-orange-600 text-2xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">大型貨車</h4>
                        <p className="text-gray-500 text-sm">5噸 / 約6坪空間</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-orange-600">$7,000</div>
                      <div className="text-gray-500 text-sm">起/車</div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">適合：大量家庭廢棄物，整屋家具清空</p>
                </div>
              </div>

              <div className="mt-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-r-xl">
                <div className="flex items-start gap-3">
                  <i className="ri-recycle-line text-green-600 text-2xl"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">♻️ 可回收物品優惠</h4>
                    <p className="text-gray-600 text-sm">
                      冰箱、洗衣機、冷氣等大型家電可折抵 $300-800
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🎯 區塊 5：搬家東西壞掉怎麼賠 - 理賠案例 */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="inline-block bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-shield-check-line mr-2"></i>
              保險理賠保障
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              <strong>搬家東西壞掉怎麼賠？</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              我們提供<strong>全程保險保障</strong>，物品損壞依規定理賠，讓您搬家更安心
            </p>
          </div>

          {/* 理賠流程 */}
          <div className="bg-white rounded-2xl p-8 shadow-xl mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              📋 理賠申請流程
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-camera-line text-green-600 text-3xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">1. 現場拍照</h4>
                <p className="text-gray-600 text-sm">發現損壞立即拍照存證</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-text-line text-green-600 text-3xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">2. 填寫申請</h4>
                <p className="text-gray-600 text-sm">填寫理賠申請表單</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-eye-line text-green-600 text-3xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">3. 評估審核</h4>
                <p className="text-gray-600 text-sm">3 天內完成損害評估</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-green-600 text-3xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">4. 快速理賠</h4>
                <p className="text-gray-600 text-sm">7 天內賠償到帳</p>
              </div>
            </div>
          </div>

          {/* 理賠案例 */}
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            📝 真實理賠案例
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {claimCases.map((caseItem, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-green-200"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <i className={`${caseItem.icon} text-green-600 text-2xl`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">{caseItem.title}</h4>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-500 mb-1">📍 狀況</div>
                    <p className="text-gray-700 text-sm">{caseItem.situation}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-500 mb-1">📋 處理流程</div>
                    <p className="text-gray-700 text-sm">{caseItem.process}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3">
                    <div className="text-sm font-semibold text-green-700 mb-1">✅ 理賠結果</div>
                    <p className="text-green-800 font-bold">{caseItem.result}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 保障說明 */}
          <div className="mt-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-8 text-center text-white shadow-xl">
            <h3 className="text-2xl font-bold mb-4">🛡️ 建鈞搬家保障承諾</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <i className="ri-shield-check-line text-3xl mb-2"></i>
                <h4 className="font-bold mb-1">全程保險</h4>
                <p className="text-white/80 text-sm">搬家責任險保障</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <i className="ri-time-line text-3xl mb-2"></i>
                <h4 className="font-bold mb-1">快速理賠</h4>
                <p className="text-white/80 text-sm">7 天內完成賠償</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <i className="ri-customer-service-line text-3xl mb-2"></i>
                <h4 className="font-bold mb-1">專人服務</h4>
                <p className="text-white/80 text-sm">專人協助理賠申請</p>
              </div>
            </div>
            <Link
              to="/knowledge/article/insurance-claims-2026"
              className="inline-flex items-center justify-center gap-2 bg-white text-green-600 px-8 py-3 rounded-full font-bold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              查看完整理賠指南
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
